import React, { useState, useEffect, useRef, useCallback } from 'react';
import moment from 'moment-timezone';
import ct from 'countries-and-timezones';
import { PrevIcon, NextIcon, TimezoneIcon, ChevronDownIcon } from '../icons';
import { Colors } from '../../constants';
import useWindowDimensions from '../../custom_hooks/useWindowDimensions';
import { useTranslation } from 'react-i18next';
import { hex_to_rgba } from '../../custom_functions/hex_to_rgba';

const BookingCalendar = (props) => {
  var week_format = moment.weekdaysShort();
  if (props.weekFormat === 'Mon') {
    week_format =  [ "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun" ];
  }
  const [ weekdaysShort ] = useState(week_format);
  const [ dateContext, setDateContext ] = useState();
  const [ calendarElements, setCalendarElements ] = useState(null);
  const [ selectedDate ] = useState(null);
  const [ timingKey, setTimingKey ] = useState(null);
  const [ currentTimeHHMM, setCurrentTimeHHMM ] = useState('');
  let intervalDateTime = useRef();
  const { t } = useTranslation();

  const {
    timingsData,
    timezoneName,
    color,
    getTimings,
    handleDayClicked,
    handleChangeTimingKey,
    handleChangeTimezone,
    hideTimingList,
    dateSelected,
    clearDayTiming,
    isoTime,
    timingSelected,
    isShareDateTime,
    shareTimings,
    weekFormat
  } = props;

  const [ timezoneVisibility, setTimezoneVisibility ] = useState('hidden');
  const [ showAllTimezone, setShowAllTimezone ] = useState(false);

  useEffect(() => {
    if (dateContext) {
      if (timingSelected && !isShareDateTime && typeof timingSelected === 'number') {
        setDateContext(moment(timingSelected));
      }
    }
  },
  // eslint-disable-next-line
  [timingSelected, dateContext, dateSelected]);

  // generate calendar
  useEffect(() => {
    if (dateContext) {
      // variable to check if the clicked day have no more time to do meeting
      let checkTimeEmpty=moment(selectedDate,'x').format("dddd, DD MMMM YYYY")==='Invalid date';

      let blanks=[];

      var spaces = dateContext.startOf('month').format('d');
      if (weekFormat === 'Mon') {
        spaces = dateContext.startOf('month').format('d') - 1
        if (spaces < 0) {
          spaces += 7;
        }
      }

      for(let i=0;i<spaces;i++){
          blanks.push(
            <div className="day empty-slot no-click" key={i*80}></div>
          );
      }

      // initializing some variable to generate the day in the calendar
      let daysInMonth=[];
      let currentMoment = moment(dateContext);
      let timingKey = currentMoment.format('YYYY').concat('-', currentMoment.format('MM'));
      let timings = (timingsData && timingsData[timingKey]) || [];

      // loop to get all the day of current clicked month
      for(let day=1;day<=dateContext.daysInMonth();day++){
          // for available time
          let day_with_format = day + " "+ dateContext.tz(timezoneName).format('M YYYY');
          let find_in_timings = timings.find(x => moment(x).tz(timezoneName).format('D M YYYY') === day_with_format);
          if(find_in_timings){
            // has timeslots
            daysInMonth.push(
              <Day
                key={day}
                checkTimeEmpty={checkTimeEmpty}
                color={color}
                onDayClick={dayOnClick}
                number={day}
                day={find_in_timings}
                dateSelected={dateSelected}
                monthYear={timingKey.split("-").reverse().join("-")}
                isShareDateTime={isShareDateTime}
                shareTimings={shareTimings} />
            );
          }
          else{
            daysInMonth.push(
              <div key={day} className="day empty-slot no-click">{day}</div>
            )
          }
      }

      // check if theres still day left that empty and change it into -
      var totalSlots=[...blanks,...daysInMonth];
      for(let a=1;a<7;a++){
          if((totalSlots.length%7) !== 0){
            totalSlots.push(
              <div key={a*Math.random()} className="day empty-slot no-click"></div>
            );
          }
          else {
            break;
          }
      }

      // make the row and cell to show the day
      let rows=[];
      let cells=[];

      totalSlots.forEach((data,i)=>{
        if((i%7) !== 0){
          // push the column
          cells.push(data);
        }
        else{
          // create new column
          let insertRow=cells.slice();
          rows.push(insertRow);
          cells=[];
          cells.push(data);
        }

        if(i === totalSlots.length-1){
          // last
          let insertRow=cells.slice();
          rows.push(insertRow);
        }
      });


      let trElems=[rows.map((d,i)=>{
          return(
            <div className="new-row" key={i*100}>
                {d}
            </div>
          )
      })];

      setCalendarElements(trElems);
      let current_date_time = moment(new Date()).tz(timezoneName);
      if(isoTime === true){
        setCurrentTimeHHMM(current_date_time.format("HH:mm"));
      }
      else{
        setCurrentTimeHHMM(current_date_time.format("hh:mm a"));
      }
    }
  },
  // eslint-disable-next-line
  [timingsData, dateContext]);

  // updated timezoneName
  useEffect(() => {
    if (dateContext) {
      // check if updated datecontext with new timezone is before than current datetime
      let check_current = moment(dateContext).tz(timezoneName).isBefore(moment());
      if(check_current){
        setDateContext(moment().tz(timezoneName));
      }
      else{
        setDateContext(moment(dateContext).tz(timezoneName));
      }

      let current_date_time = moment(new Date()).tz(timezoneName);
      intervalDateTime.current = clearInterval(intervalDateTime.current);
      if(isoTime === true){
        setCurrentTimeHHMM(current_date_time.format("HH:mm"));
      }
      else{
        setCurrentTimeHHMM(current_date_time.format("hh:mm a"));
      }

      let element = document.getElementById("current-time-hh-mm");
      if(element){
        intervalDateTime.current = setInterval(() => {
          let element_inner = '';
          if(isoTime === true){
            element_inner = moment(new Date()).tz(timezoneName).format('HH:mm');
          }
          else{
            element_inner = moment(new Date()).tz(timezoneName).format('hh:mm a');
          }
          element.innerHTML = element_inner;
        }, 1000);
      }
      else{
        intervalDateTime.current = clearInterval(intervalDateTime.current);
      }
    }
  },
  // eslint-disable-next-line
  [timezoneName]);

  // change timing key
  useEffect(() => {
    if(timingKey){
      handleChangeTimingKey(timingKey);
    }
  },
  // eslint-disable-next-line
  [timingKey]);

  useEffect(() => {
    if (dateContext) {
      // get timings if the current timingsData doesn't have the value
      let nextTimingKey = moment(dateContext).format("YYYY").concat('-', moment(dateContext).format("MM"));
      let current_date = new Date();
      if(timingsData && !timingsData[nextTimingKey] &&
        ( moment(dateContext).isSameOrAfter(moment(current_date)) ||
          moment(current_date).format("YYYY").concat('-', moment(current_date).format("MM")) === nextTimingKey ) ){
        getTimings(nextTimingKey+'-01');
      }

      setTimingKey(nextTimingKey);
    }
  },
  // eslint-disable-next-line
  [dateContext]);

  // when timezone show
  useEffect(() => {
    if(showAllTimezone){
      setTimezoneVisibility('visible');
    }
    else{
      setTimezoneVisibility('hidden');
    }
  }, [showAllTimezone]);


  useEffect(() => {
    if (!dateSelected || (dateSelected && !dateContext)) setDateContext(moment(new Date()));
    // else setDateContext(moment(new Date(dateSelected)))
    // eslint-disable-next-line
  }, [dateSelected])
  

  useEffect(() => {
    setColorToDateSelected();
  },
  // eslint-disable-next-line
  [dateSelected]);

  useEffect(() => {
    let keys = Object.keys(shareTimings);
    if (isShareDateTime) {
      if (calendarElements && dateContext && dateSelected) {
        var day_elements = document.getElementsByClassName('day active');
        for(let i = 0; i<= day_elements.length; i++){
          if(day_elements[i]){
            day_elements[i].classList.remove('selected');

            day_elements[i].style.backgroundColor = `#${Colors.white}`;
            day_elements[i].style.color = `${color}`;
          }
        }

        let date_selected_key = getDayMonthYearKey();

        if (keys.length < 7) {
          keys = [...keys, date_selected_key];
        }
        for (let j = 0; j < keys.length; j++) {
          var element = document.getElementById(`day-${keys[j]}`);
          if (element) {
            element.classList.add('selected');
            element.style.backgroundColor = hex_to_rgba(color, 0.15);
            element.style.color = `${color}`;
            element.style.borderColor = `${color}`;
          }
        }
      }

    }
  },
  // eslint-disable-next-line
  [dateContext, isShareDateTime, dateSelected, calendarElements]);

  // callback to set date selected
  const setColorToDateSelected = useCallback(() => {
    if(dateSelected){
      var key = getDayMonthYearKey();
      var element = document.getElementById(`day-${key}`);
      if(element){
        // element.style.backgroundColor = `${color}`;
        // element.style.color = `#${Colors.white}`;
        element.style.backgroundColor = `${hex_to_rgba(color, 0.15)}`;
        element.style.color = `${color}`;
        element.classList.add('selected');
      }
    }
  },
  // eslint-disable-next-line
  [dateSelected, color]);

  const getDayMonthYearKey = useCallback(() => {
    var current_date_time = new Date(dateSelected);
    var key = `${current_date_time.getDate()}-${("0" + (current_date_time.getMonth() + 1)).slice(-2)}-${current_date_time.getFullYear()}`;
    return key;
  },
  [dateSelected])

  // callback function when day clicked
  const dayOnClick = (date) => {
    handleDayClicked(date);
  };

  // callback function when user clicked previous month
  const prevMonth = () => {
    let new_date_context=Object.assign({}, dateContext);
    new_date_context=moment(dateContext).subtract(1,"month");
    setDateContext(new_date_context);
    hideTimingList();
  };

  // callback function when user clicked next month
  const nextMonth = () => {
    let new_date_context=moment(dateContext).add(1,"month");
    setDateContext(new_date_context);
    hideTimingList();
  };

  const currentMonth = useCallback(() => {
    if (dateContext) {
      return t(dateContext.format("MMMM"));
    }
    return null;
  },
  // eslint-disable-next-line
  [dateContext])

  const currentYear = useCallback(() => {
    if (dateContext) {
      return dateContext.format("Y");
    }
    return null;
  },
  [dateContext])

  const weekdays = () => {
    let weekday_name = [];
    weekdaysShort.forEach((day) =>{
      weekday_name.push(
          <div key={day} className="week-day">
            {t(day.substring(0, 2))}
          </div>
      )
    });
    return weekday_name;
  };

  // callback function to show timezone list
  const changeShowAllTimezone = () => {
    setShowAllTimezone(!showAllTimezone);
  };

  // callback function to generate timezone list
  const allTimezone = () => {
    let timezoneList=ct.getAllTimezones();
    let keys = Object.keys(timezoneList);
    let zone=[];

    if(keys.length > 0){
      keys.forEach(key => {
        zone.push(
          <li className="timezone-list" value={timezoneList[key]['name']} key={timezoneList[key]['name']}
              onClick={(e)=>{onTimezoneClick(e, timezoneList[key]['name'])}}>
              <span className="timezone-name">{timezoneList[key]['name']}</span>
              <span className="timezone-list-time">
                { (isoTime === true) ? moment(new Date()).tz(timezoneList[key]['name']).format("HH:mm") : moment(new Date()).tz(timezoneList[key]['name']).format("hh:mm a")}
              </span>
          </li>
        );
      })
    }

    return(
        <div>
            {zone}
        </div>
    )
  };

  const onTimezoneClick = (e, name) => {
    e.preventDefault();
    handleChangeTimezone(name);
    changeShowAllTimezone();
    hideTimingList();

    let key = getDayMonthYearKey();

    let element = document.getElementById(`day-${key}`);
    if(element){
      element.style.backgroundColor = `#${Colors.white}`;
      element.style.color = `${color}`;
      element.classList.remove('selected');
    }

    clearDayTiming();
  }

  const searchTimezone = (e) => {
    const listItems=document.querySelectorAll(".timezone-list");
    const listElements=[...listItems];

    const searchValue=e.target.value.toLowerCase();
    listElements.forEach(listElement=>{
        const stringFound=listElement.innerText.toLowerCase().indexOf(searchValue)!==-1;
        if(stringFound){
          listElement.style.display="flex"
        } else {
          listElement.style.display="none"
        }
    });
  };
  // ----------------- Handle return ----------------- //
  return(
    <>
      <div className="calendar-container">
        <div className="calendar">
          <div className="calendar-header">
            <div className="calendar-month-year">
              {`${currentMonth()} ${currentYear()}`}
            </div>
            <div className="calendar-month-control">
                <div className="prev-month">
                  <PrevIcon
                    handleOnClick={prevMonth} />
                </div>

                <div className="next-month p-right-0">
                  <NextIcon
                    handleOnClick={nextMonth} />
                </div>
            </div>
          </div>

          <div className="calendar-date">
            <div className="week-name">
              { weekdays() }
            </div>

            <div className="calendar-date-component">
              { calendarElements }
            </div>
          </div>
        </div>

        <Timezone
          changeShowAllTimezone={changeShowAllTimezone}
          timezoneName={timezoneName}
          currentTimeHHMM={currentTimeHHMM}
          timezoneVisibility={timezoneVisibility}
          searchTimezone={searchTimezone}
          allTimezone={allTimezone} />
      </div>
    </>
  );
};


const Day = (props) => {
  const [ borderColor, setBorderColor ] = useState(Colors.primaryColor);
  const [ fontColor, setFontColor ] = useState('');
  const [ bgColor, setBgColor ] = useState('');
  const [ isSelected, setIsSelected ] = useState(false);

  const {
    color,
    checkTimeEmpty,
    onDayClick,
    number,
    day,
    dateSelected,
    monthYear,
    isShareDateTime,
    shareTimings
  } = props;

  useEffect(() => {
    var key = '';
    if (dateSelected) {
      var current_date_time = new Date(dateSelected);
      key = `${current_date_time.getDate()}-${("0" + (current_date_time.getMonth() + 1)).slice(-2)}-${current_date_time.getFullYear()}`;
    }
    if(`${number}-${monthYear}` === key || (isShareDateTime && Object.keys(shareTimings).includes(`${number}-${monthYear}`))){
      setBgColor(`${hex_to_rgba(color, 0.15)}`);
      setBorderColor(`${color}`);
      setFontColor(`${color}`);
      setIsSelected(true);
    }
    else{
      setFontColor(color);
      setBgColor(`#${Colors.white}`);
      setBorderColor(`#${Colors.white}`);
      setIsSelected(false);
    }
  },
  // eslint-disable-next-line
  []);

  const onMouseOver=()=>{
    setBorderColor(`${color}`);
    setFontColor(`#${Colors.white}`);
    setBgColor(color);
  };

  const onMouseOut=(e)=>{
    // check if the target element is the selected date
    if(e.target.classList.contains('selected')){
      setBorderColor(`${color}`);
      setFontColor(`${color}`);
      setBgColor(`${hex_to_rgba(color, 0.15)}`);
    }
    else{
      setBorderColor(`${color}`);
      setFontColor(`${color}`);
      setBgColor(`#${Colors.white}`);
    }

  };

  return(
    <div
      data-id={day}
      data-value={`${number}-${monthYear}`}
      id={`day-${number}-${monthYear}`}
      style={checkTimeEmpty ? { backgroundColor: bgColor, borderColor: borderColor, color: fontColor}: {}}
      className={`day active ${isSelected ? 'selected' : ''}  ${checkTimeEmpty ? '' : 'empty-slot no-click'}`}
      onClick={()=>onDayClick(day)}
        onMouseOver={() => onMouseOver()} onMouseOut={(e) => onMouseOut(e)}>
        {number}
    </div>
  );
};


const Timezone = ({ changeShowAllTimezone, timezoneName, currentTimeHHMM, timezoneVisibility, searchTimezone, allTimezone  }) => {
  const { width } = useWindowDimensions();
  const { t } = useTranslation();

  return(
    <div className="timezone-wrap" id="timezone-wrap">
      <span id="dates"></span>
      <div className="button-search">
        <TimezoneIcon
          color={ `#${Colors.timezoneGray}` }
          width={ width <= 858 ? '14' : '22' }
          height={ width <= 878 ? '14' : '22' } />
        <button onClick={() => changeShowAllTimezone()} type="button" className="timezone">
            { `${timezoneName}`}
            <span id="current-time-hh-mm">
              {/* {`${moment(new Date()).tz(timezoneName).format("hh:mma")}` } */}
              { currentTimeHHMM }
            </span>
            <span>
              <ChevronDownIcon />
            </span>
        </button>
      </div>
      { timezoneVisibility === 'visible' && (
        <div className="search-timezone">
          <div className="search-header">
            <div className="input-wrap">
              <input type="text" className="search-input" placeholder={t("Search Timezone")} onChange={(value) => searchTimezone(value)} />
            </div>
            <div className="action-close">
              <button className="btn-close-timezone" type="button" onClick={() => changeShowAllTimezone()}>x</button>
            </div>
          </div>
          <ul className="search-list">
              {allTimezone()}
          </ul>
      </div>
      )}

    </div>
  );
}

export default BookingCalendar;
